// Phase RV-3 — RolloverTierPerformance footer.
//
// Per-tier row: Seeded default (always), Actual rate (only when
// resolved_slips_count ≥ 50 per N06), avg combined odds, avg
// confidence, best cycle, worst-streak.

import { Badge, Eyebrow, HairlineRow, Pill, Stat } from "../super-acca";
import type {
  RolloverTier,
  TrackerCycle,
  TrackerWinRate,
} from "../../rollover/useRolloverTracker.ts";
import {
  avgCombinedOdds,
  avgConfidence,
  bestCycle,
  longestLossStreak,
} from "./RolloverTrackerSummary.tsx";

const N06_THRESHOLD = 50;
export const ACTUAL_WIN_RATE_THRESHOLD = N06_THRESHOLD;

const TIER_ORDER: readonly RolloverTier[] = ["2x", "3x", "4x", "5x", "6x"];

function pct(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return "—";
  return `${Math.round(v * 100)}%`;
}

function num(v: number | null, digits = 2): string {
  if (v === null || !Number.isFinite(v)) return "—";
  return v.toFixed(digits);
}

export interface RolloverTierPerformanceProps {
  winRates: Record<RolloverTier, TrackerWinRate>;
  cycles: TrackerCycle[];
}

export function RolloverTierPerformance({
  winRates,
  cycles,
}: RolloverTierPerformanceProps): JSX.Element {
  return (
    <div style={{ padding: 14 }}>
      <Eyebrow>Tier Performance</Eyebrow>
      <div style={{ marginTop: 10 }}>
        {TIER_ORDER.map((tier, idx) => {
          const wr = winRates[tier] ?? {
            seeded_default: tier === "6x" ? 0.36 : 0,
            actual_win_rate: null,
            resolved_slips_count: 0,
            kelly_uses_actual: false,
          };
          const tierCycles = cycles.filter((c) => c.tier === tier);
          const actualGate = wr.resolved_slips_count >= N06_THRESHOLD;
          const needed = Math.max(0, N06_THRESHOLD - wr.resolved_slips_count);
          const avgOdds = avgCombinedOdds(tierCycles);
          const avgConf = avgConfidence(tierCycles);
          const best = bestCycle(tierCycles);
          const worstStreak = longestLossStreak(tierCycles);
          const direction = ((): string => {
            if (!actualGate || wr.actual_win_rate === null) return "";
            if (wr.actual_win_rate > wr.seeded_default) return "above";
            if (wr.actual_win_rate < wr.seeded_default) return "below";
            return "at";
          })();

          return (
            <HairlineRow key={tier} first={idx === 0} padY={12}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 64 }}>
                <Pill>{tier.toUpperCase()}</Pill>
              </div>
              <div
                style={{
                  flex: 1,
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                  gap: 14,
                }}
              >
                <Stat
                  label={actualGate ? "Actual" : "Seeded"}
                  value={actualGate ? pct(wr.actual_win_rate) : pct(wr.seeded_default)}
                  sub={
                    actualGate
                      ? `Platform default: ${pct(wr.seeded_default)} · ${direction} model`
                      : `Platform default · ${needed} more resolved slip${needed === 1 ? "" : "s"} needed for personalised rate.`
                  }
                />
                <Stat label="Avg Combined Odds" value={num(avgOdds)} />
                <Stat
                  label="Avg Confidence"
                  value={avgConf !== null ? `${avgConf.toFixed(0)}` : "—"}
                />
                <Stat
                  label="Best Cycle"
                  value={best && best.slip?.combined_odds ? `${best.slip.combined_odds.toFixed(2)}×` : "—"}
                  sub={
                    best && best.slip?.combined_odds ? (
                      <Badge variant="success">WON</Badge>
                    ) : null
                  }
                />
                <Stat
                  label="Worst Streak"
                  value={worstStreak === 0 ? "—" : `${worstStreak} losses`}
                />
              </div>
            </HairlineRow>
          );
        })}
      </div>
    </div>
  );
}
